import { cache, volumeLoader } from "@cornerstonejs/core";
const loadingVolumes = new Map();
let currentVolumeId = null;
let currentSeriesKey = null;
let loadGeneration = 0;

export const MAX_MPR_SLICES = 128;

function yieldToMain() {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

function waitTwoFrames() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function takeFullExtentSlices(imageIds, maxSlices = MAX_MPR_SLICES) {
  if (!imageIds?.length || imageIds.length <= maxSlices) {
    return imageIds || [];
  }

  const last = imageIds.length - 1;
  const sampled = [];
  let prev = -1;

  for (let i = 0; i < maxSlices; i++) {
    const index = Math.round((i * last) / (maxSlices - 1));
    if (index !== prev) {
      sampled.push(imageIds[index]);
      prev = index;
    }
  }

  return sampled;
}

export function volumeHasGeometry(volume) {
  const dims = volume?.dimensions;
  return Array.isArray(dims) && dims.length >= 3 && dims.every((d) => Number(d) > 0);
}

export function volumeHasVtkExtent(volume) {
  if (!volumeHasGeometry(volume)) return false;

  try {
    const imageData =
      volume.imageData ||
      (typeof volume.getCornerstoneImageData === "function"
        ? volume.getCornerstoneImageData()
        : null) ||
      (typeof volume.getScalarData === "function" ? true : null);

    const vtkImage =
      volume.imageData ||
      volume.vtkImageData ||
      volume._imageData;

    const extent =
      vtkImage?.getExtent?.() ||
      vtkImage?.extent ||
      volume?.vtkOpenGLTexture?.extent;

    if (extent && typeof extent[Symbol.iterator] === "function") {
      return true;
    }

    // Scalar allocation usually means VTK image data exists.
    if (imageData === true) {
      try {
        const scalar = volume.getScalarData();
        return Boolean(scalar && scalar.length > 0);
      } catch {
        return false;
      }
    }

    return volumeHasGeometry(volume);
  } catch {
    return false;
  }
}

export async function waitForVolumeExtent(volume, timeoutMs = 90000) {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (volumeHasVtkExtent(volume) || volumeHasGeometry(volume)) {
      return true;
    }
    await sleep(40);
  }
  return volumeHasGeometry(volume);
}

export function getCurrentVolumeId() {
  return currentVolumeId;
}

export function getCurrentSeriesKey() {
  return currentSeriesKey;
}

export async function detachVolumeFromEngine(engine, volumeId) {
  if (!engine || !volumeId) return;

  const viewports = engine.getViewports?.() || [];

  for (const viewport of viewports) {
    try {
      if (viewport.removeVolumeActors) {
        await Promise.resolve(viewport.removeVolumeActors([volumeId]));
      }
    } catch {
      // Viewport may already be disabled or have no actor.
    }
  }

  await waitTwoFrames();
}

export async function removeVolume(volumeId) {
  if (!volumeId) return;

  loadingVolumes.delete(volumeId);

  try {
    const volume = cache.getVolume?.(volumeId);
    volume?.cancelLoading?.();
  } catch {
    // Ignore.
  }

  try {
    if (cache.getVolume?.(volumeId)) {
      cache.removeVolumeLoadObject(volumeId);
    }
  } catch (error) {
    console.warn("Could not remove volume:", volumeId, error);
  }
}

export function purgeImageAndVolumeCache() {
  try {
    cache.purgeCache?.();
  } catch (error) {
    console.warn("Cache purge failed:", error);
  }
}

function decacheImageIds(imageIds = []) {
  imageIds.forEach((imageId) => {
    try {
      cache.removeImageLoadObject(imageId, { force: true });
    } catch {
      // Image may already be gone.
    }
  });
}

function getErrorMessage(error) {
  return String(
    error?.error?.message ||
      error?.message ||
      error ||
      ""
  );
}

export function isMissingPixelDataError(error) {
  const message = getErrorMessage(error).toLowerCase();

  return (
    message.includes("pixel data is missing") ||
    message.includes("pixel data missing")
  );
}

export async function releaseCurrentVolume(engine) {
  const volumeId = currentVolumeId;
  const volume = volumeId ? cache.getVolume(volumeId) : null;
  const imageIds = volume?.imageIds ? [...volume.imageIds] : [];

  currentVolumeId = null;
  currentSeriesKey = null;

  if (volumeId) {
    try {
      volume?.cancelLoading?.();
    } catch {
      // Ignore.
    }
    await detachVolumeFromEngine(engine, volumeId);
    await removeVolume(volumeId);
    decacheImageIds(imageIds);
  }

  try {
    cache.decacheIfNecessaryUntilBytesAvailable?.(256 * 1024 * 1024);
  } catch {
    // Ignore.
  }

  await waitTwoFrames();
}

export function startVolumePixelLoad(volume) {
  if (!volume) return Promise.resolve();
  if (volume.loadStatus?.loaded || volume.isLoaded) {
    return Promise.resolve(volume);
  }

  const start = () => {
    if (typeof volume.loadImages === "function") {
      return Promise.resolve(volume.loadImages());
    }
    if (typeof volume._prefetchImageIds === "function") {
      return Promise.resolve(volume._prefetchImageIds());
    }
    if (typeof volume.load === "function") {
      return Promise.resolve(volume.load());
    }
    return Promise.resolve(volume);
  };

  try {
    return start().catch((error) => {
      const message = String(error?.message || error || "");
      if (message.includes("CACHE_SIZE_EXCEEDED")) {
        try {
          cache.decacheIfNecessaryUntilBytesAvailable?.(
            128 * 1024 * 1024,
            volume.imageIds
          );
        } catch {
          // Ignore.
        }
        return start();
      }
      throw error;
    });
  } catch (error) {
    console.warn("Could not start volume pixel load:", error);
    return Promise.resolve(volume);
  }
}

export async function waitForLoadedFrames(volume, minFrames = 6, timeoutMs = 120000) {
  const start = Date.now();

  while (Date.now() - start < timeoutMs) {
    const processed = Number(volume?.framesProcessed || 0);
    const loaded = Number(volume?.framesLoaded || 0);
    if (
      processed >= minFrames ||
      loaded >= minFrames ||
      volume?.loadStatus?.loaded ||
      volume?.isLoaded
    ) {
      return true;
    }
    await sleep(100);
  }

  return Number(volume?.framesProcessed || volume?.framesLoaded || 0) > 0;
}

export async function getOrCreateVolume(volumeId, imageIds) {
  const existing = cache.getVolume(volumeId);
  if (existing) {
    return existing;
  }

  if (loadingVolumes.has(volumeId)) {
    return loadingVolumes.get(volumeId);
  }

  const promise = (async () => {
    return volumeLoader.createAndCacheVolume(volumeId, {
      imageIds,
    });
  })();

  loadingVolumes.set(volumeId, promise);

  try {
    return await promise;
  } finally {
    loadingVolumes.delete(volumeId);
  }
}

export async function loadSharedSeriesVolume({ engine, seriesKey, imageIds }) {
  const generation = ++loadGeneration;

  if (!engine || !seriesKey || !imageIds?.length) {
    return null;
  }

  const volumeImageIds = takeFullExtentSlices(imageIds);

  if (
    currentVolumeId &&
    currentSeriesKey === seriesKey &&
    cache.getVolume(currentVolumeId)
  ) {
    return {
      volumeId: currentVolumeId,
      volume: cache.getVolume(currentVolumeId),
      generation,
      usedSliceCount: volumeImageIds.length,
      totalSliceCount: imageIds.length,
    };
  }

  await releaseCurrentVolume(engine);
  await yieldToMain();

  if (generation !== loadGeneration) {
    return null;
  }

  const volumeId = `cornerstoneStreamingImageVolume:${seriesKey}_${volumeImageIds.length}`;
  const volume = await getOrCreateVolume(volumeId, volumeImageIds);

  if (generation !== loadGeneration) {
    await detachVolumeFromEngine(engine, volumeId);
    await removeVolume(volumeId);
    return null;
  }

  currentVolumeId = volumeId;
  currentSeriesKey = seriesKey;

  return {
    volumeId,
    volume,
    generation,
    usedSliceCount: volumeImageIds.length,
    totalSliceCount: imageIds.length,
  };
}

export function isVolumeLoadCurrent(generation) {
  return generation === loadGeneration;
}

export function cancelPendingVolumeLoads() {
  loadGeneration += 1;
}

export function viewportHasRenderableVolume(viewport) {
  try {
    const imageData = viewport?.getImageData?.();
    const dims = imageData?.dimensions;
    if (!dims || dims.some((d) => !d)) return false;

    const vtkImage = imageData?.imageData;
    const extent = vtkImage?.getExtent?.() || vtkImage?.extent;
    if (extent && typeof extent[Symbol.iterator] !== "function") {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

export function safeResetCamera(viewport) {
  if (!viewportHasRenderableVolume(viewport)) return false;

  try {
    viewport.resetCamera();
    return true;
  } catch (error) {
    console.warn("resetCamera skipped:", error);
    return false;
  }
}
